/**
 * EVENTSFLO-CREW-PLANNER-ASSIGNMENT-STATUS-ICONS-V1
 *
 * Pure helper + tiny chip component that maps a `shift_crew_assignments`
 * row to a single, human-readable status pill rendered alongside an
 * assigned crew name.
 *
 * Inputs (all optional; only what's present is consulted):
 *   - assignment.status           ("pending"|"accepted"|"rejected"|"cancelled"|"crew_cancelled")
 *   - assignment.pushed_at        (ISO timestamp from POST /…/push)
 *   - assignment.responded_at     (set when crew accepts/rejects)
 *   - assignment.confirmed        (admin marked confirmed)
 *   - assignment.worked           (admin marked worked)
 *   - assignment.onboarding_override (boolean)
 *   - assignment.seen_at          (when crew opened the request)
 *   - assignment.cancelled_at     (set when admin cancels OR crew withdraws)
 *   - assignment.previous_status  (the status before cancellation/withdrawal)
 *   - assignment.cancel_reason    (free-text crew withdrawal reason)
 *
 * Resolution priority (highest → lowest):
 *   1. worked          → "Worked"
 *   2. confirmed       → "Confirmed"
 *   3. status=crew_cancelled → "Withdrawn by crew"
 *   4. status=cancelled→ "Cancelled"
 *   5. status=rejected → "Rejected"
 *   6. status=accepted → "Accepted"
 *   7. status=pending  → "Pending"
 *   8. pushed_at set   → "Sent"   (legacy rows that were pushed but never
 *                                   set status explicitly)
 *   9. else            → "Assigned"
 *
 * The onboarding override is rendered as a SEPARATE small triangle next to
 * the main chip — it can co-exist with any state.
 */
import React from "react";
import {
  Check,
  CheckCheck,
  Clock,
  Send,
  X,
  AlertTriangle,
  Slash,
  UserCheck,
} from "lucide-react";

const STATES = {
  worked: {
    label: "Worked",
    Icon: CheckCheck,
    cls: "bg-emerald-600/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/40",
    tooltip: "Marked worked",
  },
  confirmed: {
    label: "Confirmed",
    Icon: UserCheck,
    cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40",
    tooltip: "Confirmed by admin",
  },
  cancelled: {
    label: "Cancelled",
    Icon: Slash,
    cls: "bg-zinc-500/15 text-zinc-600 dark:text-zinc-400 border-zinc-500/40",
    tooltip: "Cancelled",
  },
  crew_cancelled: {
    label: "Withdrawn by crew",
    Icon: Slash,
    cls: "bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/40",
    tooltip: "Withdrawn by crew",
  },
  rejected: {
    label: "Rejected",
    Icon: X,
    cls: "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/40",
    tooltip: "Rejected by crew",
  },
  accepted: {
    label: "Accepted",
    Icon: Check,
    cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40",
    tooltip: "Accepted by crew",
  },
  pending: {
    label: "Pending",
    Icon: Clock,
    cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40",
    tooltip: "Request sent to app — awaiting response",
  },
  sent: {
    label: "Sent",
    Icon: Send,
    cls: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/40",
    tooltip: "Request sent to app",
  },
  assigned: {
    label: "Assigned",
    Icon: null,
    cls: "bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/30",
    tooltip: "Assigned (not yet pushed to app)",
  },
};

/** Pure resolver — exported for unit tests. */
export function resolveAssignmentStatus(a) {
  if (!a) return "assigned";
  if (a.worked === true) return "worked";
  if (a.confirmed === true) return "confirmed";
  const s = (a.status || "").toLowerCase();
  if (s === "crew_cancelled") return "crew_cancelled";
  if (s === "cancelled" || s === "declined_by_admin") return "cancelled";
  if (s === "rejected" || s === "declined") return "rejected";
  if (s === "accepted") return "accepted";
  if (s === "pending") return "pending";
  if (a.pushed_at) return "sent";
  return "assigned";
}

/**
 * Tooltip that adds context (timestamps + override) on top of the
 * baseline tooltip from the STATES table.
 */
function buildTooltip(state, a) {
  const base = STATES[state]?.tooltip || "";
  const parts = [base];
  if (a?.pushed_at && (state === "sent" || state === "pending")) {
    parts.push(`Pushed at ${new Date(a.pushed_at).toLocaleString()}`);
  }
  if (a?.responded_at && (state === "accepted" || state === "rejected")) {
    parts.push(`Responded at ${new Date(a.responded_at).toLocaleString()}`);
  }
  // EVENTSFLO-CREW-PWA-WITHDRAW-AND-AUDIT-V1: enrich tooltip on crew withdrawal
  if (state === "crew_cancelled") {
    if (a?.cancelled_at) {
      parts.push(`Withdrawn at ${new Date(a.cancelled_at).toLocaleString()}`);
    }
    if (a?.staff_name) {
      parts.push(`By ${a.staff_name}`);
    }
    if (a?.previous_status) {
      parts.push(`Previously: ${a.previous_status}`);
    }
    if (a?.cancel_reason) {
      parts.push(`Reason: ${a.cancel_reason}`);
    }
  }
  if (state === "cancelled") {
    if (a?.cancelled_at) {
      parts.push(`Cancelled at ${new Date(a.cancelled_at).toLocaleString()}`);
    }
    if (a?.previous_status) {
      parts.push(`Previously: ${a.previous_status}`);
    }
  }
  if (a?.seen_at) {
    parts.push(`Seen at ${new Date(a.seen_at).toLocaleString()}`);
  }
  if (a?.onboarding_override) {
    parts.push("Onboarding override used");
  }
  return parts.filter(Boolean).join(" · ");
}

/**
 * Tiny pill rendered next to the assigned crew name.
 *
 * Props:
 *  - assignment: the shift_crew_assignment row.
 *  - scale: visual scale factor (Sheet planner zoom).
 *  - showLabel: when true, render the textual label too. When false the
 *    pill is icon-only — used in the SHEET planner where space is tight.
 */
export function AssignmentStatusBadge({ assignment, scale = 1, showLabel = false, className = "" }) {
  const state = resolveAssignmentStatus(assignment);
  const cfg = STATES[state] || STATES.assigned;
  const tooltip = buildTooltip(state, assignment);
  const fontSize = Math.round(9 * scale);
  const padX = Math.round(showLabel ? 5 : 3 * scale);
  const iconSize = Math.max(8, Math.round(9 * scale));
  const Icon = cfg.Icon;
  return (
    <span
      data-testid={`assignment-status-${state}-${assignment?.staff_id || "unknown"}`}
      data-state={state}
      title={tooltip}
      className={[
        "shrink-0 inline-flex items-center gap-1 rounded-full border leading-none whitespace-nowrap",
        cfg.cls,
        className,
      ].join(" ")}
      style={{
        fontSize: `${fontSize}px`,
        padding: `${Math.round(2 * scale)}px ${padX}px`,
      }}
    >
      {Icon ? <Icon style={{ width: iconSize, height: iconSize }} /> : null}
      {showLabel ? <span>{cfg.label}</span> : null}
    </span>
  );
}

/** Small amber triangle for override — rendered as a sibling of the main badge. */
export function OnboardingOverrideBadge({ assignment, scale = 1 }) {
  if (!assignment?.onboarding_override) return null;
  const iconSize = Math.max(8, Math.round(9 * scale));
  return (
    <span
      data-testid={`assignment-onboarding-override-${assignment.staff_id || "unknown"}`}
      title="Onboarding override used — crew assigned despite incomplete readiness."
      className="shrink-0 inline-flex items-center text-amber-500"
    >
      <AlertTriangle style={{ width: iconSize, height: iconSize }} />
    </span>
  );
}

export default AssignmentStatusBadge;
